

import dayjs from 'dayjs';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { NavigateFunction, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { APIProvider } from '@vis.gl/react-google-maps';
import { BySubmitter, SiteEventType, Species } from '../../../api/dtos/dtos';
import AuthContext from '../../../contexts/auth/AuthContext';
import MapContext, { AUTHORIZED_MAP_CONTEXT, DATAACCESS_MAP_CONTEXT, DEFAULT_MAP_CONTEXT, GROUPADMIN_MAP_CONTEXT, LayersFilter, MapCenter, MapEvents, MapFilter, MapFilterPeriodEnum, MapFilterSubmitter, RecordTypeFilter, ReportingPeriodFilter, SubmitterFilter } from '../../../contexts/data/MapContext';
import MapFormContext, { RecordToEdit } from '../../../contexts/data/MapFormContext';
import { SpeciesContext } from '../../../contexts/data/SpeciesContext';
import UserGroupsContext from '../../../contexts/data/UserGroupsContext';
import { IHasChildren } from '../../common/types/IHasChildren';
import MapFilterDrawer, { DEFAULT_GROUP_MEMBER_ID } from '../../drawers/MapFilterDrawer';
import MapFormDrawer from '../../drawers/MapFormDrawer';
import SpeciesFormProvider from '../data/SpeciesFormsProvider';


interface IMapProviderProps extends IHasChildren { }

export class MapFilterUrlParams {
    private static mapParams: string[] = [
        'period',
        'from',
        'to',
        'year',
        'monthYear',
        'displayHeatmap',
        'submitter',
        'locatedInRegionIds',
        'dataAccessId',
        'sighting',
        'damage',
        'control',
        'management',
        'disease',
        'communityGroupId',
        'communityGroupMemberId',
        'groupid',
        'chart7',
        'chart31',
        'subSpeciesId'
    ]

    static getURLParams(state: MapFilter): string {
        let queryParams: string = "?";

        queryParams += this.queryPeriodState(state);
        queryParams += `&${this.querySubmitterState(state)}`;
        queryParams += `&${this.queryRecordTypeState(state)}`;
        queryParams += `&${this.queryLayersState(state)}`;

        return queryParams;
    }

    static getState(queryParameters: URLSearchParams, navigate?: NavigateFunction, key?: string) {
        return {
            reportingPeriod: this.getPeriodState(queryParameters, navigate, key),
            bySubmitter: this.getSubmitterState(queryParameters),
            recordType: this.getRecordTypeState(queryParameters),
            layers: this.getLayersState(queryParameters)
        }
    }

    static hasMapParams(queryParameters: URLSearchParams) {
        if (!queryParameters ||
            queryParameters.size === 0) {
            return false;
        }

        for (let i = 0; i < MapFilterUrlParams.mapParams.length; i++) {
            if (queryParameters.has(MapFilterUrlParams.mapParams[i])) {
                return true;
            }
        }

        return false;
    }

    private static getPeriodState(queryParams: URLSearchParams, navigate?: NavigateFunction, key?: string): ReportingPeriodFilter {
        let period = queryParams.get("period");
        let last7 = queryParams.get("chart7");
        let last31 = queryParams.get("chart31");
        if (last7 === "true" || last31 === "true") {
            period = last7 === "true" 
                ? MapFilterPeriodEnum.LastSevenDays.toString() 
                : MapFilterPeriodEnum.LastThirtyOneDays.toString();
            if (navigate && key) {
                navigate(`/${key}/chartsandtables?${queryParams.toString()}`, { replace: true });
            }
        }

        return {
            period: this.parseMapFilterPeriodEnum(period),
            fromField: queryParams.get("from") ? dayjs(queryParams.get("from"), "YYYY/MM/DD") : DEFAULT_MAP_CONTEXT.filter.reportingPeriod.fromField,
            toField: queryParams.get("to") ? dayjs(queryParams.get("to"), "YYYY/MM/DD") : DEFAULT_MAP_CONTEXT.filter.reportingPeriod.toField,
            yearField: queryParams.get("year") ? dayjs(queryParams.get("year"), 'YYYY') : DEFAULT_MAP_CONTEXT.filter.reportingPeriod.yearField,
            monthYearField: queryParams.get("monthYear") ? dayjs(queryParams.get("monthYear"), 'MMMM YYYY') : DEFAULT_MAP_CONTEXT.filter.reportingPeriod.monthYearField
        }
    }

    private static getSubmitterState(queryParams: URLSearchParams): SubmitterFilter {
        let submitter = queryParams.get("submitter");
        let communityGroupId = queryParams.get("communityGroupId");
        let communityGroupMemberId = queryParams.get("communityGroupMemberId");
        let locatedInRegionIds = queryParams.get("locatedInRegionIds");
        let dataAccessId = queryParams.get("dataAccessId");

        let groupId = queryParams.get("groupid");

        if (groupId) {
            communityGroupId = groupId;
            if (!communityGroupMemberId) {
                communityGroupMemberId = "-1";
            }

            if (!submitter) {
                submitter = MapFilterSubmitter.MyGroup.toString();
            }
        }

        return {
            submitter: this.parseMapFilterSubmitternum(submitter),
            communityGroupId: communityGroupId != null ? parseInt(communityGroupId) : undefined,
            communityGroupMemberId: communityGroupMemberId != null ? parseInt(communityGroupMemberId) : undefined,
            displayHeatmap: queryParams.get("displayHeatmap") === null ?
                true :
                queryParams.get("displayHeatmap") === "true",
            locatedInRegionIds: locatedInRegionIds
                ? locatedInRegionIds
                    .split(',')
                    .map(id => Number(id))
                    .filter(id => !Number.isNaN(id))
                : undefined,
            dataAccessId: dataAccessId ? +dataAccessId : undefined
        }
    }

    private static getRecordTypeState(queryParams: URLSearchParams): RecordTypeFilter {
        var subSpeciesId = queryParams.get("subSpeciesId");
        return {
            sighting: queryParams.get("sighting") === null ?
                true :
                queryParams.get("sighting") === "true",
            damage: queryParams.get("damage") === null ?
                true :
                queryParams.get("damage") === "true",
            control: queryParams.get("control") === null ?
                true :
                queryParams.get("control") === "true",
            disease: queryParams.get("disease") === null ?
                false :
                queryParams.get("disease") === "true",
            subSpeciesId: subSpeciesId ? +subSpeciesId : undefined,
            externallySourcedData: null
        }
    }

    private static getLayersState(queryParams: URLSearchParams): LayersFilter {
        return {
            communityGroupKmls: [],
            selectedCommunityGroupKmls: queryParams.get("selectedCommunityGroupKmls")?.split(",")?.map(x => +x) ?? [],
            barrierFences: queryParams.get("barrierFences") === null ?
                true :
                queryParams.get("barrierFences") === "true",
            knownRegions: queryParams.get("knownRegions") === null ?
                true :
                queryParams.get("knownRegions") === "true",
            releaseSites: queryParams.get("releaseSites") === null ?
                true :
                queryParams.get("releaseSites") === "true",
            rhdBoostRollOutSites: queryParams.get("rhdBoostRollOutSites") === null ?
                true :
                queryParams.get("rhdBoostRollOutSites") === "true",
            successfulEoiSites: queryParams.get("successfulEoiSites") === null ?
                true :
                queryParams.get("successfulEoiSites") === "true",
        }
    }

    private static parseMapFilterPeriodEnum(value: string | null) {
        if (!value) {
            return MapFilterPeriodEnum.DateRange
        }

        let newValue = parseInt(value);

        if (isNaN(newValue) || !Object.values(MapFilterPeriodEnum).includes(newValue)) {
            newValue = MapFilterPeriodEnum.DateRange
        }

        return newValue;
    }

    private static parseMapFilterSubmitternum(value: string | null) {
        if (!value) {
            return MapFilterSubmitter.AllData
        }

        let newValue = parseInt(value);

        if (isNaN(newValue) || !Object.values(MapFilterSubmitter).includes(newValue)) {
            newValue = MapFilterSubmitter.AllData
        }

        return newValue;
    }

    private static queryPeriodState(state: MapFilter): string {
        let params: string = "";
        if (state.reportingPeriod) {
            switch (state.reportingPeriod.period) {
                case MapFilterPeriodEnum.DateRange:
                    params += `period=${state.reportingPeriod.period}`;
                    if (state.reportingPeriod.fromField) {
                        params += `&from=${state.reportingPeriod.fromField.format('YYYY/MM/DD')}`;
                    }
                    if (state.reportingPeriod.toField) {
                        params += `&to=${state.reportingPeriod.toField.format('YYYY/MM/DD')}`;
                    }

                    break;

                case MapFilterPeriodEnum.Year:
                    params += `period=${state.reportingPeriod.period}`;
                    if (state.reportingPeriod.yearField) {
                        params += `&year=${state.reportingPeriod.yearField.format('YYYY')}`;
                    }

                    break;

                case MapFilterPeriodEnum.MonthYear:
                    params += `period=${state.reportingPeriod.period}`;
                    if (state.reportingPeriod.monthYearField) {
                        params += `&monthYear=${state.reportingPeriod.monthYearField.format('MMMM YYYY')}`;
                    }

                    break;

                case MapFilterPeriodEnum.LastSevenDays:
                case MapFilterPeriodEnum.LastThirtyOneDays:
                    params += `period=${state.reportingPeriod.period}`;

                    break;

                default: return params;
            }
        }

        return params;
    }

    private static querySubmitterState(state: MapFilter): string {
        let params: string = "";
        if (state.bySubmitter) {
            switch (state.bySubmitter.submitter) {
                case MapFilterSubmitter.PublicData:
                    params += `submitter=${state.bySubmitter.submitter}`;
                    params += state.bySubmitter.displayHeatmap ? '&displayHeatmap=true' : '';

                    break;

                case MapFilterSubmitter.AllData:
                    params += `submitter=${state.bySubmitter.submitter}${state.bySubmitter?.locatedInRegionIds && state.bySubmitter?.locatedInRegionIds?.length > 0 ? '&locatedInRegionIds=' + state.bySubmitter.locatedInRegionIds.join(',') : '' }${state.bySubmitter?.dataAccessId ? '&dataAccessId=' + state.bySubmitter.dataAccessId : '' }`;
                    break;

                case MapFilterSubmitter.MyData:
                    params += `submitter=${state.bySubmitter.submitter}`;
                    break;

                case MapFilterSubmitter.MyGroup:
                    params += `submitter=${state.bySubmitter.submitter}&communityGroupId=${state.bySubmitter.communityGroupId}&communityGroupMemberId=${state.bySubmitter.communityGroupMemberId}`;

                    break;

                default: return params;
            }
        }

        return params;
    }

    private static queryRecordTypeState(state: MapFilter): string {
        let params: string = "";
        if (state.recordType) {
            if (state.recordType.sighting !== null) {
                params += `sighting=${state.recordType.sighting}`;
            }

            if (state.recordType.damage !== null) {
                params += `${params.length > 0 ? "&" : ""}damage=${state.recordType.damage}`;
            }

            if (state.recordType.control !== null) {
                params += `${params.length > 0 ? "&" : ""}control=${state.recordType.control}`;
            }

            if (state.recordType.disease !== null) {
                params += `${params.length > 0 ? "&" : ""}disease=${state.recordType.disease}`;
            }

            if (state.recordType.subSpeciesId !== null) {
                params += `${params.length > 0 ? "&" : ""}subSpeciesId=${state.recordType.subSpeciesId}`;
            }
        }

        return params;
    }

    private static queryLayersState(state: MapFilter): string {
        let params: string = "";

        if (state.layers.barrierFences !== null) {
            params += `barrierFences=${state.layers.barrierFences}`;
        }

        if (state.layers.knownRegions !== null) {
            params += `${params.length > 0 ? "&" : ""}knownRegions=${state.layers.knownRegions}`;
        }

        params += state.layers.selectedCommunityGroupKmls.join(",");
        return params;
    }
}

const MapProvider: FC<IMapProviderProps> = ({
    children
}) => {
    //
    // -- Hooks --
    const [queryParameters] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    //
    // -- Contexts --
    const { user } = useContext(AuthContext);
    const { data: userGroupsConfig } = useContext(UserGroupsContext);
    const { species: { species } } = useContext(SpeciesContext);

    const isAdminOrSuperAdmin = useMemo(() => {
        return !!user?.roles?.find(x => x === 'admin' || x === 'superadmin');
    }, [user]);

    const isDataAccess = useMemo(() => {
        return !!user?.roles?.find(x => x === 'dataaccess');
    }, [user]);

    const isDataAccessForThisSpecies = useMemo(() => {
        return (
            user?.matchingRegionAdminRoles?.some(
                x => x.speciesId == null || x.speciesId === species
            ) ?? false
        );
    }, [user?.matchingRegionAdminRoles, species]);

    const communityGroups = useMemo(() => {
        const groups = userGroupsConfig?.communityGroups ?? [];

        const userId = user?.id;

        return groups
            .filter(g => {
                const admin = g.adminUserId == userId;
                const members = g.communityGroupMembers;
                const speciesList = g.communityGroupSpecies;

                const hasAccess = members?.some(
                    m => m.userId === userId && m.viewRights
                ) || admin || isAdminOrSuperAdmin;

                if (!hasAccess) return false;

                if (!speciesList || speciesList.length === 0) return true;

                return speciesList.some(
                    cgs =>
                        (cgs.speciesId === species && cgs.linkGroupToSpecies === 1) ||
                        cgs.linkGroupToSpecies === 0
                );
            })
            .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    }, [isAdminOrSuperAdmin, userGroupsConfig?.communityGroups, user?.id, species]);

    //
    // -- States --
    const [formDrawerOpen, setFormDrawerOpen] = useState<boolean>(false);
    const [viewHistoryRoot, setViewHistoryRoot] = useState<number | undefined>(undefined);
    const [viewHistorySiteEventType, setViewHistorySiteEventType] = useState<SiteEventType | undefined>(undefined);
    const [siteEventType, setSiteEventType] = useState<SiteEventType | undefined>(undefined);
    const [mapFormData, setMapFormData] = useState<Record<string, any>>({});
    const [formDrawerMaximised, setFormDrawerMaximised] = useState<boolean>(false);
    const [selectingLocationCoords, setSelectingLocationCoords] = useState<{ lat: number, lng: number } | undefined>(undefined);
    const [recordToEdit, setRecordToEdit] = useState<RecordToEdit | null>(null);
    const [subSpecies, setSubSpecies] = useState<string | undefined>(undefined);
    const [imageGuid, setImageGuid] = useState<string | undefined>(undefined);
    const [accuracyWarning, setAccuracyWarning] = useState<boolean>(false);

    const [filterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
    const [periodFilter, setPeriodFilter] = useState<ReportingPeriodFilter>(
        user == null ?
            { ...DEFAULT_MAP_CONTEXT.filter.reportingPeriod } :
            user?.roles?.length === 1 && user?.roles?.includes("groupadmin") ?
                { ...GROUPADMIN_MAP_CONTEXT.filter.reportingPeriod } :
                { ...AUTHORIZED_MAP_CONTEXT.filter.reportingPeriod }
    );
    const [submitterFilter, setSubmitterFilter] = useState<SubmitterFilter>(
        user == null ?
            { ...DEFAULT_MAP_CONTEXT.filter.bySubmitter } :
            user?.roles?.length === 1 && user?.roles?.includes("groupadmin") ?
                { ...GROUPADMIN_MAP_CONTEXT.filter.bySubmitter } :
                { ...AUTHORIZED_MAP_CONTEXT.filter.bySubmitter }
    );
    const [recordTypeFilter, setRecordTypeFilter] = useState<RecordTypeFilter>(
        user == null ?
            { ...DEFAULT_MAP_CONTEXT.filter.recordType } :
            user?.roles?.length === 1 && user?.roles?.includes("groupadmin") ?
                { ...GROUPADMIN_MAP_CONTEXT.filter.recordType } :
                { ...AUTHORIZED_MAP_CONTEXT.filter.recordType }
    );
    const [layersFilter, setLayersFilter] = useState<LayersFilter>(
        user == null ?
            { ...DEFAULT_MAP_CONTEXT.filter.layers } :
            user?.roles?.length === 1 && user?.roles?.includes("groupadmin") ?
                { ...GROUPADMIN_MAP_CONTEXT.filter.layers } :
                { ...AUTHORIZED_MAP_CONTEXT.filter.layers }
    );

    // Species/User map filter defaults
    useEffect(() => {
        switch (species) {
            case Species.NewPests:
            case Species.OtherPests:
            case Species.Deer:
            case Species.FeralGoat:
            case Species.FeralFish:
            case Species.Starling:
                if (user) {
                    if (user?.roles?.length === 1 && user?.roles?.includes("groupadmin")) {
                        setPeriodFilter({
                            period: MapFilterPeriodEnum.DateRange,
                            fromField: dayjs("01-01-2010", "DD-MM-YYYY"),
                            toField: dayjs(),
                            yearField: dayjs(),
                            monthYearField: dayjs()
                        });
                        setRecordTypeFilter({ ...GROUPADMIN_MAP_CONTEXT.filter.recordType });
                        // setLayersFilter({ ...GROUPADMIN_MAP_CONTEXT.filter.layers });
                        if (communityGroups && communityGroups.length > 0) {
                            setSubmitterFilter({
                                submitter: MapFilterSubmitter.MyGroup,
                                displayHeatmap: false,
                                communityGroupId: communityGroups[0].communityGroupId,
                                communityGroupMemberId: DEFAULT_GROUP_MEMBER_ID
                            });
                        }
                        else {
                            setSubmitterFilter({ ...GROUPADMIN_MAP_CONTEXT.filter.bySubmitter });
                        }
                    }
                    else if (isDataAccess) {
                        setPeriodFilter({
                            period: MapFilterPeriodEnum.DateRange,
                            fromField: dayjs("01-01-2010", "DD-MM-YYYY"),
                            toField: dayjs(),
                            yearField: dayjs(),
                            monthYearField: dayjs()
                        });
                        if (isDataAccessForThisSpecies) {
                            setSubmitterFilter({ ...DATAACCESS_MAP_CONTEXT.filter.bySubmitter });
                        }
                        else {
                            setSubmitterFilter({ 
                                ...DATAACCESS_MAP_CONTEXT.filter.bySubmitter, 
                                submitter: BySubmitter.PublicData, 
                                submitterLabel: undefined 
                            });
                        }
                        setRecordTypeFilter({ ...DATAACCESS_MAP_CONTEXT.filter.recordType });
                        // setLayersFilter({ ...DATAACCESS_MAP_CONTEXT.filter.layers });
                    }
                    else {
                        setPeriodFilter({
                            period: MapFilterPeriodEnum.DateRange,
                            fromField: dayjs("01-01-2010", "DD-MM-YYYY"),
                            toField: dayjs(),
                            yearField: dayjs(),
                            monthYearField: dayjs()
                        });
                        setRecordTypeFilter({ ...AUTHORIZED_MAP_CONTEXT.filter.recordType });
                        // setLayersFilter({ ...AUTHORIZED_MAP_CONTEXT.filter.layers });
                        if (user?.roles?.includes("admin") || user?.roles?.includes("superadmin")) {
                            setSubmitterFilter({
                                ...AUTHORIZED_MAP_CONTEXT.filter.bySubmitter,
                                submitter: MapFilterSubmitter.AllData
                            })
                        }
                        else {
                            setSubmitterFilter({ ...AUTHORIZED_MAP_CONTEXT.filter.bySubmitter });
                        }
                    }
                }
                else {
                    setPeriodFilter({
                        period: MapFilterPeriodEnum.DateRange,
                        fromField: dayjs("01-01-2010", "DD-MM-YYYY"),
                        toField: dayjs(),
                        yearField: dayjs(),
                        monthYearField: dayjs()
                    });
                    setSubmitterFilter({ ...DEFAULT_MAP_CONTEXT.filter.bySubmitter });
                    setRecordTypeFilter({ ...DEFAULT_MAP_CONTEXT.filter.recordType });
                    // setLayersFilter({ ...DEFAULT_MAP_CONTEXT.filter.layers });
                }
                break;
            default:
                if (user) {
                    if (user?.roles?.includes("groupadmin")) {
                        setPeriodFilter({ ...GROUPADMIN_MAP_CONTEXT.filter.reportingPeriod });
                        setRecordTypeFilter({ ...GROUPADMIN_MAP_CONTEXT.filter.recordType });
                        // setLayersFilter({ ...GROUPADMIN_MAP_CONTEXT.filter.layers });
                        if (communityGroups && communityGroups.length > 0) {
                            setSubmitterFilter({
                                submitter: MapFilterSubmitter.MyGroup,
                                submitterLabel: communityGroups[0]?.name,
                                displayHeatmap: false,
                                communityGroupId: communityGroups[0].communityGroupId,
                                communityGroupMemberId: DEFAULT_GROUP_MEMBER_ID
                            });
                        }
                        else {
                            setSubmitterFilter({ ...GROUPADMIN_MAP_CONTEXT.filter.bySubmitter });
                        }
                    }
                    else if (isDataAccess) {
                        setPeriodFilter({ ...DATAACCESS_MAP_CONTEXT.filter.reportingPeriod });
                        if (isDataAccessForThisSpecies) {
                            setSubmitterFilter({ ...DATAACCESS_MAP_CONTEXT.filter.bySubmitter });
                        }
                        else {
                            setSubmitterFilter({ 
                                ...DATAACCESS_MAP_CONTEXT.filter.bySubmitter, 
                                submitter: BySubmitter.PublicData, 
                                submitterLabel: undefined 
                            });
                        }
                        setRecordTypeFilter({ ...DATAACCESS_MAP_CONTEXT.filter.recordType });
                        // setLayersFilter({ ...DATAACCESS_MAP_CONTEXT.filter.layers });
                    }
                    else {
                        setPeriodFilter({ ...AUTHORIZED_MAP_CONTEXT.filter.reportingPeriod });
                        setRecordTypeFilter({ ...AUTHORIZED_MAP_CONTEXT.filter.recordType });
                        // setLayersFilter({ ...AUTHORIZED_MAP_CONTEXT.filter.layers });
                        if (user?.roles?.includes("admin") || user?.roles?.includes("superadmin")) {
                            setSubmitterFilter({
                                ...AUTHORIZED_MAP_CONTEXT.filter.bySubmitter,
                                submitter: MapFilterSubmitter.AllData
                            })
                        }
                        else {
                            setSubmitterFilter({ ...AUTHORIZED_MAP_CONTEXT.filter.bySubmitter });
                        }
                    }
                }
                else {
                    setPeriodFilter({ ...DEFAULT_MAP_CONTEXT.filter.reportingPeriod });
                    setSubmitterFilter({ ...DEFAULT_MAP_CONTEXT.filter.bySubmitter });
                    setRecordTypeFilter({ ...DEFAULT_MAP_CONTEXT.filter.recordType });
                    // setLayersFilter({ ...DEFAULT_MAP_CONTEXT.filter.layers });
                }
                break;
        }
    }, [
        user,
        setPeriodFilter,
        setSubmitterFilter,
        setRecordTypeFilter,
        // setLayersFilter,
        species,
        isDataAccess
    ]);

    const [mapCenter, setMapCenter] = useState<MapCenter | undefined>(undefined);

    const onDrawerClose = useCallback(() => {
        setFormDrawerOpen(false);
        setSiteEventType(undefined);
        setMapFormData({});
        setViewHistoryRoot(undefined);
        setViewHistorySiteEventType(undefined);
        setFormDrawerMaximised(false);
        setSelectingLocationCoords(undefined);
        setRecordToEdit(null);
        setImageGuid(undefined);
    }, [
        setSiteEventType,
        setFormDrawerOpen,
        setMapFormData,
        setViewHistoryRoot,
        setViewHistorySiteEventType,
        setFormDrawerMaximised,
        setSelectingLocationCoords,
        setRecordToEdit,
        setImageGuid
    ]);

    const mapEvents = useMemo(() => {
        return new MapEvents();
    }, [])

    const filter = useMemo(() => {
        return ({
            reportingPeriod: periodFilter,
            bySubmitter: submitterFilter,
            recordType: recordTypeFilter,
            layers: layersFilter
        });
    }, [
        periodFilter,
        submitterFilter,
        recordTypeFilter,
        layersFilter
    ])

    const context = useMemo(() => {
        return {
            ...DEFAULT_MAP_CONTEXT,
            drawerOpen: filterDrawerOpen,
            filter,
            setPeriodFilter: setPeriodFilter,
            setSubmitterFilter: setSubmitterFilter,
            setRecordTypeFilter: setRecordTypeFilter,
            setLayersFilter: setLayersFilter,
            openDrawer: () => setFilterDrawerOpen(true),
            closeDrawer: () => setFilterDrawerOpen(false),
            events: mapEvents,
            mapCenter: mapCenter,
            setMapCenter: setMapCenter,
        }
    }, [
        filter,
        filterDrawerOpen,
        setFilterDrawerOpen,
        setPeriodFilter,
        setSubmitterFilter,
        setRecordTypeFilter,
        setLayersFilter,
        mapEvents,
        mapCenter,
        setMapCenter
    ]);

    const formContext = useMemo(() => {
        return {
            drawerOpen: formDrawerOpen,
            siteEventType: siteEventType,
            viewHistoryRoot: viewHistoryRoot,
            viewHistorySiteEventType: viewHistorySiteEventType,
            mapFormData: mapFormData,
            drawerMaximised: formDrawerMaximised,
            selectingLocationCoords: selectingLocationCoords,
            recordToEdit: recordToEdit,
            subSpecies: subSpecies,
            imageGuid: imageGuid,
            accuracyWarning: accuracyWarning,
            openDrawer: () => setFormDrawerOpen(true),
            closeDrawer: onDrawerClose,
            minimiseDrawer: () => setFormDrawerMaximised(false),
            maximiseDrawer: () => setFormDrawerMaximised(true),
            setViewHistoryRoot: setViewHistoryRoot,
            setViewHistorySiteEventType: setViewHistorySiteEventType,
            setSiteEventType: setSiteEventType,
            setMapFormData: setMapFormData,
            setSelectingLocationCoords: setSelectingLocationCoords,
            setRecordToEdit: setRecordToEdit,
            setSubSpecies: setSubSpecies,
            setImageGuid: setImageGuid,
            setAccuracyWarning: setAccuracyWarning
        };
    }, [
        formDrawerOpen,
        setFormDrawerOpen,
        siteEventType,
        setSiteEventType,
        viewHistoryRoot,
        setViewHistoryRoot,
        viewHistorySiteEventType,
        setViewHistorySiteEventType,
        mapFormData,
        setMapFormData,
        formDrawerMaximised,
        setFormDrawerMaximised,
        selectingLocationCoords,
        setSelectingLocationCoords,
        recordToEdit,
        setRecordToEdit,
        subSpecies,
        setSubSpecies,
        imageGuid,
        setImageGuid,
        accuracyWarning,
        setAccuracyWarning
    ]);

    // 
    // -- useEffects --
    //
    // Validate the filter based on request params in the Url
    useEffect(() => {
        if (MapFilterUrlParams.hasMapParams(queryParameters)) {
            const state = MapFilterUrlParams.getState(queryParameters, navigate, params["species"]);
            setPeriodFilter(state.reportingPeriod);
            setSubmitterFilter(state.bySubmitter);
            setRecordTypeFilter(state.recordType);
            //setLayersFilter(state.layers);
        }
    }, [queryParameters, navigate, params]);

    // Validate the user is logged in
    useEffect(() => {
        if (!user) {
            setSubmitterFilter(c => ({
                ...c,
                submitter: MapFilterSubmitter.PublicData
            }));
        }
    }, [user]);

    // Close form drawer when the user leaves the map page
    useEffect(() => {
        if (formDrawerOpen && !location.pathname.includes('map')) {
            setFormDrawerOpen(false);
        }
    }, [location, formDrawerOpen, setFormDrawerOpen]);

    return (
        <MapContext.Provider value={context}>
            <MapFormContext.Provider value={formContext}>
                <APIProvider apiKey={'AIzaSyBjIguT8eLjvlWvnQgfnUcK0fuqnwxbp2E'} libraries={["marker"]}>
                    <SpeciesFormProvider>
                        {children}
                        <MapFilterDrawer />
                        <MapFormDrawer />
                    </SpeciesFormProvider>
                </APIProvider>
            </MapFormContext.Provider>
        </MapContext.Provider>
    )
}

export default MapProvider